import { AlertCircle, Undo2 } from "lucide-react";
import { useId, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit } from "../types/walletDebits.types";

const REASON_MAX = 500;

/**
 * Gives a reserved debit's amount back when the manual send did not happen. Behind the second factor, like
 * reserving it; asked twice it answers the cancelled debit, so a lost answer cannot cancel anything else.
 */
export function WalletDebitCancel({
  debit,
  onChange,
}: {
  debit: WalletDebit;
  onChange: (debit: WalletDebit) => void;
}) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const inputId = useId();
  const twoFactor = useTwoFactorAction<WalletDebit>();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();

  const confirm = async () => {
    if (!trimmed) return;
    setError(null);
    try {
      const cancelled = await twoFactor.start((code) => WalletDebitsService.cancel(debit.id, trimmed, code));
      if (cancelled) onChange(cancelled);
    } catch (cancelError: unknown) {
      setError(HttpApiService.getErrorMessage(cancelError));
    }
  };

  if (!open) {
    return (
      <ColocaButton size="sm" variant="ghost" icon={Undo2} onClick={() => setOpen(true)}>
        {t("cancel_open")}
      </ColocaButton>
    );
  }

  return (
    <section className="space-y-3 rounded-xl border border-destructive/30 px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t("cancel_title")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("cancel_hint", {
            amount: `${formatCurrencyAmount(debit.amount, debit.currency, activeLanguage)} ${debit.currency}`,
          })}
        </p>
      </div>
      <div className="space-y-1.5">
        <label htmlFor={inputId} className="text-xs text-muted-foreground">
          {t("cancel_reason")}
        </label>
        <ColocaTextarea
          id={inputId}
          value={reason}
          onValueChange={setReason}
          maxLength={REASON_MAX}
          rows={2}
          placeholder={t("cancel_reason_placeholder")}
          disabled={twoFactor.isRunning}
        />
      </div>
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      <div className="flex gap-2">
        <ColocaButton
          size="sm"
          variant="destructive"
          loading={twoFactor.isRunning}
          disabled={!trimmed || twoFactor.isRunning}
          onClick={() => void confirm()}
        >
          {t("cancel_confirm")}
        </ColocaButton>
        <ColocaButton
          size="sm"
          variant="ghost"
          disabled={twoFactor.isRunning}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          {t("cancel_back")}
        </ColocaButton>
      </div>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("two_factor_required")}
        challengeDescription={t("cancel_two_factor", { reference: debit.reference })}
      />
    </section>
  );
}
